import _extends from '@babel/runtime/helpers/esm/extends';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import React, { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { mergeRefs } from '@elliemae/ds-utilities/system';
import { debounce, isNaN } from '@elliemae/ds-utilities/utils';
import NoResults from '../../components/NoResults.js';
import VirtualizedBodyRow from './VirtualizedBodyRow.js';
import { RowSizes } from '../../rowSizes.js';
import { isSafari } from './helper.js';
import '@babel/runtime/helpers/esm/objectWithoutProperties';
import 'styled-components';
import '@elliemae/ds-system';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var wrapperSafari = {};
var wrapperGeneric = {
  overflowY: 'auto',
  overflowX: 'visible'
};

var VirtualizedBody = function VirtualizedBody(props) {
  var autoScrollToId = props.autoScrollToId,
      columns = props.columns,
      Component = props.component,
      expandable = props.expandable,
      innerRef = props.innerRef,
      innerBody = props.innerBody,
      isPlaceholderActive = props.isPlaceholderActive,
      listProps = props.listProps,
      noResultsPlaceholder = props.noResultsPlaceholder,
      overscanCount = props.overscanCount,
      rows = props.rows,
      defaultRowHeight = props.rowHeight,
      rowKey = props.rowKey,
      rowRenderer = props.rowRenderer,
      rowSize = props.rowSize,
      setHasScroll = props.setHasScroll,
      filters = props.filters;
  var rowHeight = defaultRowHeight || RowSizes[rowSize];

  var _useState = useState(0),
      _useState2 = _slicedToArray(_useState, 2),
      scrollTop = _useState2[0],
      setScrollTop = _useState2[1];

  var _useState3 = useState(0),
      _useState4 = _slicedToArray(_useState3, 2),
      visibleHeight = _useState4[0],
      setVisibleHeight = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      hasScrollBar = _useState6[0],
      setHasScrollBar = _useState6[1];

  var rowStyleCache = useRef({});

  var _useState7 = useState([0, 30]),
      _useState8 = _slicedToArray(_useState7, 2),
      renderedIndexes = _useState8[0],
      setRenderedIndexes = _useState8[1];

  var getCalculatedHeightBetweenIndexes = function getCalculatedHeightBetweenIndexes(start, end) {
    var _listProps$itemSize = listProps.itemSize,
        itemSize = _listProps$itemSize === void 0 ? function () {
      return rowHeight;
    } : _listProps$itemSize;

    if (expandable) {
      return rows.slice(start, end).map(function (_, idx) {
        return idx;
      }).map(function (idx) {
        return itemSize(idx);
      }).reduce(function (acc, curr) {
        return acc + curr;
      }, 0);
    }

    return (end - start) * rowHeight;
  };

  var generateRowPositionMap = function generateRowPositionMap() {
    return rows.map(function (row, idx) {
      return getCalculatedHeightBetweenIndexes(0, idx);
    });
  };

  var rowPositionMap = useRef(generateRowPositionMap());

  var _useState9 = useState(null),
      _useState10 = _slicedToArray(_useState9, 2),
      virtualizedBodyRef = _useState10[0],
      setVirtualizedBodyRef = _useState10[1];

  var handlerRef = useRef();
  var autoScrollTo = useCallback(function (scrollHeight) {
    var _listProps$itemSize2 = listProps.itemSize,
        itemSize = _listProps$itemSize2 === void 0 ? function () {
      return rowHeight;
    } : _listProps$itemSize2;
    var idx = rows.findIndex(function (item) {
      return item[rowKey] === autoScrollToId;
    });
    var val = rowPositionMap.current[idx] || 0;
    var lastPositionIdx = rowPositionMap.current.length - 1;
    var lastPosition = 0;

    if (lastPositionIdx > -1) {
      lastPosition = rowPositionMap.current[lastPositionIdx] - scrollHeight + itemSize(lastPositionIdx);
    }

    if (lastPosition < val) {
      val = lastPosition;
    }

    if (virtualizedBodyRef) {
      if (idx > -1) {
        setScrollTop(val);
        virtualizedBodyRef.scrollTop = val;
      }
    }
  }, [rows, virtualizedBodyRef]);
  useEffect(function () {
    autoScrollTo(visibleHeight);
  }, [autoScrollToId, visibleHeight]);
  var handler = useCallback(debounce(function (_ref) {
    var target = _ref.target;
    // Update coordinates
    setScrollTop(target.scrollTop || 0);
  }, 100), [setScrollTop]);
  useEffect(function () {
    setScrollTop(0);
  }, [filters]);
  useEffect(function () {
    handlerRef.current = handler;
  }, [handler]);
  useLayoutEffect(function () {
    var top = Math.ceil(scrollTop / rowHeight);
    var bot = Math.ceil((scrollTop + visibleHeight) / rowHeight);

    if (expandable) {
      // updateMapperFirst
      rowPositionMap.current = generateRowPositionMap();
      top = rowPositionMap.current.findIndex(function (item) {
        return item > scrollTop;
      });
      bot = rowPositionMap.current.findIndex(function (item) {
        return item > scrollTop + visibleHeight;
      });
      if (bot === -1) bot = rowPositionMap.current.length;
    }

    var upperBound = top - 1 - overscanCount;
    var lowerBound = bot - 1 + overscanCount;

    if (upperBound < 0) {
      upperBound = 0;
    }

    if (lowerBound - upperBound < overscanCount) {
      lowerBound = upperBound + overscanCount;
    }

    if (lowerBound > rows.length) {
      lowerBound = rows.length;
    }

    setHasScrollBar(visibleHeight < getHeight());
    setHasScroll(visibleHeight < getHeight());
    setRenderedIndexes([upperBound, lowerBound]);
  }, [visibleHeight, scrollTop, rows]);
  useLayoutEffect(function () {
    if (virtualizedBodyRef) {
      var parsedVisibleHeight = parseFloat(window.getComputedStyle(virtualizedBodyRef, null).getPropertyValue('height')); // eslint-disable-next-line no-restricted-globals

      if (!isNaN(parsedVisibleHeight)) {
        setVisibleHeight(parsedVisibleHeight);
        autoScrollTo(parsedVisibleHeight);
      }
    }
  }, [rows, virtualizedBodyRef]);
  useEffect(function () {
    if (virtualizedBodyRef) {
      var eventListener = function eventListener(event) {
        return handlerRef.current(event);
      };

      virtualizedBodyRef.addEventListener('scroll', eventListener);
      return function () {
        virtualizedBodyRef.removeEventListener('scroll', eventListener);
      };
    }

    return function () {
      return null;
    };
  }, [virtualizedBodyRef]);

  var getHeight = function getHeight() {
    var returnHeight = 0;

    if (expandable) {
      returnHeight = getCalculatedHeightBetweenIndexes(0, rows.length) + 8;
    } else {
      returnHeight = rows.length * rowHeight + 8;
    } // if (visibleHeight > returnHeight) {
    //   return visibleHeight;
    // }


    if (virtualizedBodyRef && virtualizedBodyRef.scrollHeight > virtualizedBodyRef.clientHeight) {
      return returnHeight + 10;
    }

    return returnHeight;
  };

  var getRowStyle = function getRowStyle(row, index, specificRowHeight) {
    var itemSize = listProps.itemSize;

    if (!expandable && rowStyleCache.current[index]) {
      return rowStyleCache.current[index];
    }

    rowStyleCache.current[index] = {
      height: itemSize ? itemSize(index) : specificRowHeight,
      left: 0,
      right: 0,
      top: getCalculatedHeightBetweenIndexes(0, index),
      position: 'absolute'
    };
    return rowStyleCache.current[index];
  };

  var generateRenderedRows = function generateRenderedRows() {
    return rows.slice(renderedIndexes[0], renderedIndexes[1] + 1).map(function (_, index) {
      return /*#__PURE__*/React.createElement(VirtualizedBodyRow, {
        data: {
          rows: rows,
          columns: columns,
          rowKey: rowKey,
          renderer: {
            rowRenderer: rowRenderer
          }
        },
        index: renderedIndexes[0] + index,
        key: rows[renderedIndexes[0] + index][rowKey],
        style: getRowStyle(rows[renderedIndexes[0] + index], renderedIndexes[0] + index, rowHeight)
      });
    });
  };

  var wrapperStyle = _objectSpread({
    height: '100%'
  }, isSafari ? wrapperSafari : wrapperGeneric);

  var bodyStyle = {
    height: getHeight(),
    willChange: 'transform',
    backfaceVisibility: 'hidden'
  };
  return /*#__PURE__*/React.createElement(Component, _extends({}, props, {
    innerRef: innerRef
  }), !isPlaceholderActive ? /*#__PURE__*/React.createElement("div", {
    className: "virtualized-body-wrapper",
    style: wrapperStyle,
    ref: function ref(elem) {
      if (elem) {
        if (isSafari) {
          setVirtualizedBodyRef(elem.parentElement); // eslint-disable-next-line no-param-reassign

          elem.parentElement.scrollTop = scrollTop;
          mergeRefs(listProps.outerRef)(elem.parentElement);
        } else {
          setVirtualizedBodyRef(elem);
          mergeRefs(listProps.outerRef)(elem);
        }
      }
    }
  }, /*#__PURE__*/React.createElement("div", {
    "data-testid": "virtualized-body",
    className: "virtualized-body".concat(hasScrollBar ? ' with-scrollbar' : ''),
    style: bodyStyle,
    ref: function ref(r) {
      mergeRefs(innerBody)(r);
    }
  }, generateRenderedRows())) : /*#__PURE__*/React.createElement(NoResults, {
    innerRef: innerRef,
    rowRenderer: rowRenderer
  }, noResultsPlaceholder));
};

VirtualizedBody.defaultProps = {
  rows: [],
  overscanCount: 10
};
VirtualizedBody.propTypes = {
  autoScrollToId: PropTypes.number,
  columns: PropTypes.arrayOf(PropTypes.shape({})),
  component: PropTypes.element,
  expandable: PropTypes.bool,
  filters: PropTypes.any,
  innerRef: PropTypes.any,
  innerBody: PropTypes.any,
  isPlaceholderActive: PropTypes.bool,
  listProps: PropTypes.shape({
    itemSize: PropTypes.func,
    outerRef: PropTypes.any
  }),
  noResultsPlaceholder: PropTypes.oneOf([PropTypes.string, PropTypes.element]),
  overscanCount: PropTypes.number,
  rows: PropTypes.arrayOf(PropTypes.shape({})),
  rowHeight: PropTypes.number,
  rowKey: PropTypes.string,
  rowRenderer: PropTypes.element,
  rowSize: PropTypes.any,
  setHasScroll: PropTypes.func
};

export default VirtualizedBody;
//# sourceMappingURL=VirtualizedBody.js.map
